import React, { useState } from "react";
import { TrashIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import moment from "moment";
import ModalUpdateSubscription from "./subscription_update_modal";
import Swal from "sweetalert2";
import { calculateTotalCost, calculatePackageCost } from "../services/utils";
import ProductionQuantityLimitsOutlinedIcon from '@mui/icons-material/ProductionQuantityLimitsOutlined';

const SubscriptionManager = ({ 
    token, 
    customerPortal, 
    onBack,
    activeEmployeesNumber, 
    subscriptionDetails,
    onDelete,
    onDeletePackage,
    customerType,
    tiers,
    onSubscriptionChanged,
    discountedPkgPrice,
 }) => {
    const subscriptionStatusMap = {
        trialing: 'In prova',
        active: 'Attivo',
        incomplete: 'Incompleto',
        incomplete_expired: 'Incompleto scaduto',
        past_due: 'Pagamento insoluto',
        canceled: 'Cancellato',
        unpaid: 'Non pagato',
        cancel_at_period_end: 'Cancellato alla fine del periodo',
        disputed: 'Contestato',
    };

    const statusUI = {
        ATTIVO: 'bg-emerald-100 text-emerald-600',
        'IN PROVA': 'bg-sky-100 text-sky-600',
        'PAGAMENTO INSOLUTO': 'bg-rose-100 text-rose-600',
        'CANCELLATO ALLA FINE DEL PERIODO': 'bg-amber-100 text-amber-600',
        INCOMPLETO: 'bg-amber-100 text-amber-600',
        'INCOMPLETO SCADUTO': 'bg-amber-100 text-amber-600',
        CANCELLATO: 'bg-gray-200 text-gray-500',
        'NON PAGATO': 'bg-gray-200 text-gray-500',
        CONTESTATO: 'bg-rose-100 text-rose-600',
    };

    const getStatus = (status) => {
        const label = (subscriptionStatusMap[status] || status || '').toUpperCase();
        return {
            label,
            className: statusUI[label] || 'bg-gray-100 text-gray-600',
        };
    };

    const [editing, setEditing] = useState(null); 
    const [newQuantity, setNewQuantity] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newPrice, setNewPrice] = useState('');
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [updatedQuantity, setUpdatedQuantity] = useState(null);
    const [pendingChange, setPendingChange] = useState(null);

    const getPackagePlan = (pkg) => ({
        ...(pkg.plan || {}),
        amount: pkg.plan?.amount ?? pkg.amount,
        meta: pkg.plan?.meta ?? pkg.meta ?? {},
        tiers: pkg.plan?.tiers ?? pkg.tiers ?? [],
        is_per_seat: pkg.plan?.is_per_seat ?? pkg.is_per_seat,
    });

    const getPackagePriceInfo = (pkg) => {
        if (pkg.price !== undefined && pkg.price !== null) {
            return {
                amountCost: Number(pkg.price),
                regularCost: null,
                hasPromo: false,
            };
        }

        return calculatePackageCost(
            getPackagePlan(pkg),
            pkg.quantity || activeEmployeesNumber || 1,
            customerType
        );
    };

    const renderPackagePrice = (pkg) => {
        const { amountCost, regularCost, hasPromo } = getPackagePriceInfo(pkg);

        return (
            <div>
                {hasPromo && regularCost !== null && (
                    <div className="text-xs text-gray-500 line-through">
                        {formatPriceIT(regularCost)}
                    </div>
                )}

                <div className="flex items-center gap-2">
                    {hasPromo && <Badge text="PROMO" />}
                    <span>{formatPriceIT(amountCost)}</span>
                </div>
            </div>
        );
    };

    const handleSetEditing = (id, currentQuantity) => {
        setEditing(id);
        setNewQuantity(currentQuantity);
    };

    const clamp = (n) => Math.min(500, Math.max(1, n));

    const handleQuantityChange = (e) => {
        const val = e.target.value;
        if (val === '') return setNewQuantity('');
        const n = parseInt(val, 10);
        if (Number.isNaN(n)) return;
        setNewQuantity(n);
    };

    const handleQuantityBlur = () => {
        if (newQuantity === '') return setNewQuantity(1);
        setNewQuantity(clamp(newQuantity));
    };

    const onConfirm = () => {
      if (newQuantity === '') return setNewQuantity(1);
      const q = clamp(newQuantity);
      setNewQuantity(q);
      if (editing) handleUpdateQuantity(editing);
    };

    const handleUpdateQuantity = (id) => {
        const numericQuantity = parseInt(newQuantity, 10);   
        const subscription = subscriptionDetails.find((sub) => sub.id === id);

        if (!subscription) {
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: 'Dettagli della sottoscrizione non trovati.',
                confirmButtonText: 'OK',
            });
            return;
        }
    
        const currentQuantity = subscription.quantity;
    
        if (Number.isNaN(numericQuantity)) {
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: 'Inserisci un numero valido.',
                confirmButtonText: 'OK',
            });
            return;
        }
    
        if (numericQuantity === currentQuantity) {
            Swal.fire({
                icon: 'info',
                title: 'Numero di licenze invariato',
                html: `La quantità inserita, <strong>${numericQuantity}</strong>, è uguale alla quantità attuale.`,
                confirmButtonText: 'OK',
                allowOutsideClick: false,
            });
            return;
        }
    
        if (numericQuantity < activeEmployeesNumber) {
            Swal.fire({
                icon: 'error',
                title: 'Numero di licenze insufficiente',
                html: `Hai <strong>${activeEmployeesNumber}</strong> contratti attivi.<br><br> Non è possibile ridurre il numero di licenze sotto il numero di contratti attivi.`,
                confirmButtonText: 'OK',
                allowOutsideClick: false,
            });
            return;
        }

        const tiersArray = tiers?.[id]?.tiers;
        
        if (!tiersArray || tiersArray.length === 0) {
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: 'Tiers non trovati per la sottoscrizione selezionata.',
                confirmButtonText: 'OK',
            });
            return;
        }        
        const newPriceResult = calculateTotalCost(numericQuantity, tiersArray, customerType);

        setSelectedPlan(subscription);
        setPendingChange({ id, quantity: numericQuantity });
        setNewPrice(newPriceResult);
        
        setIsModalOpen(true);
        setEditing(null);
    };

    const handleCancelEdit = () => {
        setEditing(null);
    };

    const handleUpdateEmployeesNumber = (newQuantity) => {
        setUpdatedQuantity(newQuantity);
    };

    const formatPriceIT = (value) => {
        const num = new Intl.NumberFormat('it-IT', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value ?? 0);

        return `€ ${num}`;
    };

    const Info = ({ label, value, extra, note }) => (
        <div className="rounded-lg bg-gray-50 p-4 shadow-sm border">
            <dt className="text-xs font-semibold text-gray-500 uppercase">{label}</dt>

            <dd className="mt-1 flex items-center justify-between text-sm font-medium text-gray-800 leading-snug">
                {value || "-"}
                {extra}
            </dd>

            {note && (
                <p className="pt-1 text-[11px] italic text-gray-500 leading-snug">
                    {note}
                </p>
            )}
        </div>
    );

    const Badge = ({ text }) => (
        <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-xs font-bold text-orange-600">
            {text}
        </span>
    );


    const Action = ({ txt, icon, onClick, variant = 'primary' }) => {
        const common = 'inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-semibold shadow transition';
        const styles = {
            primary:   'border border-indigo-500 text-indigo-600 hover:bg-indigo-600 hover:text-white',
            secondary: 'border border-gray-400  text-gray-600  hover:bg-gray-100',
            danger:    'border border-red-500   text-red-600   hover:bg-red-600   hover:text-white',
        };
        return (
            <button onClick={onClick} className={`${common} ${styles[variant]}`}>
            {txt} {icon}
            </button>
        );
    };

    return (
        <div className="mx-auto mt-6 w-full max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex justify-end mt-4">
                {/* <button
                    className="border block rounded-md px-3 py-2 mr-6 text-center text-sm font-semibold text-grey-800 shadow-sm hover:bg-grey-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-grey-600"
                    onClick={onBack}
                >
                    Torna indietro
                </button> */}
            </div>
            {/* ─────────── WRAPPER ─────────── */}
            <div className="mb-10 bg-white/70 backdrop-blur rounded-3xl shadow-xl ring-1 ring-gray-100">
            <div className="divide-y divide-gray-200">

                {/* ► 1)  CARDS SOTTOSCRIZIONE  */}
                {subscriptionDetails?.length ? (
                subscriptionDetails.map((d, i) => {
                    const status = getStatus(d.status);
                    const deltaDays = moment().diff(
                        moment(d?.current_period_start),
                        "days"
                    );

                    const viewAlertPastDue =
                        d?.status === "past_due" &&
                        d?.current_period_start &&
                        deltaDays <= 15;

                    const activePackages = Array.isArray(d.packages)
                        ? d.packages
                        : [];

                    return (
                    <React.Fragment key={d.id || i}>
                    <section className="p-6 sm:p-8 group hover:bg-gray-50/40 transition">
                        {/* header */}
                        <header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <h3 className="text-lg sm:text-xl font-semibold text-indigo-800 flex items-center gap-2">
                                <span className="text-base font-normal text-indigo-500">Piano</span>
                                {d.plan?.name || ''}
                            </h3>

                            <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-bold uppercase ${status.className}`}>
                                {status.label}
                            </span>
                        </header>
                        {viewAlertPastDue && (
                            <div className="w-full pt-2 text-xs text-right">
                                Stiamo processando il tuo pagamento. <br />
                                Se lo stato <span className="text-red-500 font-semibold">"Insoluto" </span> 
                                non avrà variazioni entro 15 gg dall'inizio della sottoscrizione, contattaci.
                            </div>
                        )}

                        {/* grid dettagli */}
                        <dl className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            <Info label="Codice sottoscrizione" value={d.stripe_subscription_id} />
                            <Info label="Durata (mesi)" value={d.plan?.duration_months} />
                            {d.status !== 'cancel_at_period_end' && (
                                <Info
                                    label="Prezzo (IVA inclusa) al prossimo rinnovo"
                                    value={formatPriceIT(d.price)}
                                    extra={
                                        d.coupon && (
                                        <Badge text={`SCONTO ${d.coupon}`} />
                                        )
                                    }
                                    note={
                                        <>
                                            {d.coupon && (
                                            <>
                                                Il prezzo indicato è già comprensivo dello sconto previsto dal coupon applicato.
                                                <br />
                                            </>
                                            )}
                                            L'importo del prossimo rinnovo include anche eventuali crediti o debiti maturati.
                                        </>
                                    }
                                />
                            )}

                            <Info label={
                                d.status === 'trialing'
                                    ? 'Termine prova'
                                    : d.status === 'cancel_at_period_end'
                                    ? 'Termine periodo'
                                    : 'Prossimo rinnovo'
                                }
                                value={d.current_period_end && moment(d.current_period_end).format('DD/MM/YYYY')}
                            />

                            <Info label="Licenze dipendenti" value={updatedQuantity ?? d.quantity} />

                            {/* azioni */}
                            <div className="col-span-full flex flex-wrap gap-2 pt-2">
                                {d.status === 'active' && (
                                <>
                                    <Action
                                        txt="Modifica licenze"
                                        icon={<PencilSquareIcon className="w-4 h-4" />}
                                        onClick={() => handleSetEditing(d.id, d.quantity)}
                                    />
                                    <Action
                                        txt="Elimina"
                                        icon={<TrashIcon className="w-4 h-4" />}
                                        onClick={() => onDelete(d.id)}
                                        variant="danger"
                                    />
                                </>
                                )}
                            </div>
                        </dl>

                        {/* sezione edit quantità */}
                        {editing === d.id && (
                        <div className="mt-6 rounded-lg border border-indigo-200 p-4 bg-indigo-50/30">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                            Nuova quantità
                            </label>
                            <div className="flex items-end gap-2">
                                <input
                                    type="number"
                                    min={1}
                                    max={500}
                                    step={1}
                                    value={newQuantity}
                                    onChange={handleQuantityChange}
                                    onBlur={handleQuantityBlur}
                                    className="w-24 rounded-md border-gray-300 px-3 py-1.5 shadow-sm focus:border-indigo-600 focus:ring-indigo-600"
                                />
                                <Action txt="Conferma" onClick={onConfirm} />
                                <Action txt="Annulla" onClick={handleCancelEdit} variant="secondary" />
                            </div>
                        </div>
                        )}
                    </section>

                    {activePackages.map((pkg) => {
                        const pkgStatus = getStatus(pkg.status);

                        return (
                            <section
                                key={`package-${pkg.id}`}
                                className="p-6 sm:p-8 group hover:bg-gray-50/40 transition"
                            >
                                <header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                <h3 className="text-lg sm:text-xl font-semibold text-indigo-800 flex items-center gap-2">
                                    <span className="text-base font-normal text-indigo-500">
                                    Pacchetto
                                    </span>
                                    {pkg.plan?.name || ""}
                                </h3>

                                <span
                                    className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-bold uppercase ${pkgStatus.className}`}
                                >
                                    {pkgStatus.label}
                                </span>
                                </header>

                                <dl className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                <Info
                                    label="Codice sottoscrizione"
                                    value={pkg.stripe_subscription_item_id}
                                />

                                <Info
                                    label="Durata (mesi)"
                                    value={pkg.plan?.duration_months}
                                />

                                <Info label="Quantità" value={pkg.quantity} />

                                {pkg.status !== 'cancel_at_period_end' && (
                                    <Info
                                        label="Prezzo pacchetto (IVA inclusa) al prossimo rinnovo"
                                        value={renderPackagePrice(pkg)}
                                        // extra={
                                        //     d.coupon && (
                                        //         <Badge text={`SCONTO ${d.coupon}`} />
                                        //     )
                                        // }
                                        note={
                                            <>
                                                {pkg.plan?.meta?.regular_price && (
                                                    <>
                                                        Prezzo promozionale applicato.
                                                        <br />
                                                    </>
                                                )}
                                                {pkg.coupon && (
                                                    <>
                                                        Il prezzo indicato è già comprensivo dello sconto previsto dal coupon applicato.
                                                        <br />
                                                    </>
                                                )}
                                                L'importo del prossimo rinnovo include anche eventuali crediti o debiti maturati.
                                            </>
                                        }
                                    />
                                )}

                                <Info
                                    label={
                                    pkg.status === "trialing"
                                        ? "Termine prova"
                                        : pkg.status === "cancel_at_period_end"
                                        ? "Termine periodo"
                                        : "Prossimo rinnovo"
                                    }
                                    value={
                                    pkg.current_period_end &&
                                    moment(pkg.current_period_end).format("DD/MM/YYYY")
                                    }
                                />

                                <div className="col-span-full flex flex-wrap gap-2 pt-2">
                                    {pkg.status === "active" && (
                                    <Action
                                        txt="Elimina"
                                        icon={<TrashIcon className="w-4 h-4" />}
                                        onClick={() =>
                                        onDeletePackage
                                            ? onDeletePackage(pkg.id)
                                            : onDelete(pkg.id)
                                        }
                                        variant="danger"
                                    />
                                    )}
                                </div>
                                </dl>
                            </section>
                        );
                    })}
                    </React.Fragment>
                    );
                })
                ) : (
                /* ► 2)  EMPTY-STATE  */
                <div className="flex flex-col items-center py-24">
                    {/* icona */}
                    <ProductionQuantityLimitsOutlinedIcon className="text-indigo-200" style={{ height: '4rem', width: '4rem' }} />

                    {/* testo */}
                    <p className="mt-6 text-base font-medium text-gray-500">
                        Nessuna sottoscrizione trovata
                    </p>
                </div>
                )}
            </div>

            {/* ► 3)  CTA pagamento */}
            <div className="border-t border-gray-200 p-6 flex justify-center">
                <a
                href={customerPortal}
                rel="noopener noreferrer"
                className="rounded-xl bg-indigo-600 px-6 py-3 text-sm font-bold text-white shadow-md
                            hover:bg-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-300/60"
                >
                Gestisci metodo di pagamento
                </a>
            </div>
            </div>

            {isModalOpen && pendingChange && (
                <ModalUpdateSubscription
                    mode='updateSubscription'
                    token={token} 
                    selectedPlan={selectedPlan}
                    newQuantity={pendingChange} 
                    onClose={() => setIsModalOpen(false)}
                    newPrice={newPrice}
                    onUpdateQuantity={handleUpdateEmployeesNumber}
                    onSubscriptionChanged={onSubscriptionChanged}
                />
            )}
            {/* <ModalUpdateSubscription
                    mode='newSubscription'
                    selectedPlan={selectedPlan}
                    employeeCount={employeeCount}
                    duration={duration}
                    selectedPrice={selectedPrice}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                    onClose={handleCloseModal}
                    token={token}
                /> */}
        </div>
    );
};

export default SubscriptionManager;
